import type {
  Block, DecideReason, FormBlock, FormField, FormLine, FormPanel, RowColumn,
} from '@/kova/spec/types'
import type { ApiClient } from '@/lib/api'

import type { Caller } from './handles'
import { rowsFrom, submitWrite, writeRefusal } from './writes'

/**
 * The six writes on the routing table, split across four permissions.
 *
 * # Two pairs, and the console must not collapse them
 *
 * `offering.read` and `offering.write` are **Ops** — switching a degraded rail
 * off at 3am is the queue task an analyst does. `offering.buy_price.read` and
 * `offering.buy_price.publish` are **Finance**, *"because what an institution
 * charges US is a transcription of a tariff annex rather than a decision
 * anybody here makes."*
 *
 * So an Ops session gets four live controls and two dead ones naming Finance,
 * and a Finance session gets the reverse. Both sets are drawn (law 18): each is
 * somebody's, and an operator who can see the control they lack knows who to
 * ask.
 *
 * # Law 6, honoured in two of its three parts, and the third one FILED
 *
 * The amend and buy-price controls are labelled **"Save as new version"** and
 * seed every field from what is live, so an operator edits the version in force
 * rather than retyping it from nothing.
 *
 * They do **not** offer an `effective_from`, and that is not an oversight. No
 * write path on this family accepts one: the bodies carry *"no version, no row
 * id and no effective dates"*, deliberately, because a body that could name an
 * effective date could name a **past** one and that is a rewrite of an issued
 * term. A version governs from the instant it is written.
 *
 * Offering the field and discarding it before the request would be worse than
 * omitting it — the operator would believe they had scheduled something. So the
 * control says the version governs from now, says scheduling is not available,
 * and cites the backend issue: **go-wallet#897**.
 *
 * # Nothing here is defaulted, and every default has a legal zero
 *
 * `available`, `min_amount_minor`, `cutoffs`, `flat_minor`, `percent_bps`,
 * `hour`, `minute` and `settlement_lag_days` are each required by the backend
 * and refused when absent, because every one of them has a zero that means
 * something an operator would not choose by accident: 00:00 accepts almost
 * nothing on the day, a settlement lag of 0 promises same-day, a minimum of 0
 * accepts amounts nobody agreed to accept, and an omitted `available` read as
 * false *"would take a live rail dark and report success."*
 *
 * # The cutoff week is the WHOLE week, never a delta
 *
 * *"An omitted week read as empty would take a live rail dark."* The control
 * says so at the field, and an empty week is offered as a real answer — it is
 * what a withdrawn rail looks like — rather than being indistinguishable from a
 * console that forgot.
 *
 * A LIST rather than a map keyed by weekday, matching the wire: *"an object
 * would make a repeated weekday resolve silently to whichever came last."* A
 * duplicate is refused here too, before the request, rather than being left for
 * the 400.
 */

/** `offering.write` — define, amend, retire, reinstate. Ops. */
const TERMS_PERM = 'offering.write'
const TERMS_ROLE = 'Ops'

/** `offering.buy_price.publish` — the tariff. Finance's alone. */
const PRICE_PERM = 'offering.buy_price.publish'
const PRICE_ROLE = 'Finance'

const DIRECTIONS: readonly (readonly [value: string, label: string])[] = [
  ['in', 'in — money arriving on this rail'],
  ['out', 'out — money leaving on it'],
]

/**
 * `floor` or `ceil`, and never a default.
 *
 * *"A defaulted rounding rule is a cost that is systematically wrong in one
 * direction by at most one minor unit, on every transaction, invisibly."* Our
 * sell side always floors because flooring favours the customer; the buy side
 * is whatever the counterparty's schedule says, and assuming floor when they
 * round up understates our cost on every transaction.
 */
const ROUNDINGS: readonly (readonly [value: string, label: string])[] = [
  ['floor', 'floor — they truncate the proportional part downwards'],
  ['ceil', 'ceil — they round it up'],
]

const WEEKDAYS: readonly (readonly [value: string, label: string])[] = [
  ['monday', 'monday'], ['tuesday', 'tuesday'], ['wednesday', 'wednesday'],
  ['thursday', 'thursday'], ['friday', 'friday'], ['saturday', 'saturday'],
  ['sunday', 'sunday'],
]

/**
 * The cutoff week, as declared columns.
 *
 * Names rather than ISO numbers, matching the wire and for the reason the
 * backend gives: *"the two numberings in play differ by one for six days and by
 * six for the seventh […] so a number crossing the boundary is a payment booked
 * a day late at whichever end got the conversion wrong. A name cannot be off by
 * one."*
 */
const CUTOFF_COLUMNS: readonly RowColumn[] = [
  { name: 'weekday', label: 'Weekday', kind: 'select', required: true, options: WEEKDAYS, width: '150px' },
  { name: 'hour', label: 'Hour', kind: 'text', required: true, width: '90px', placeholder: '16' },
  { name: 'minute', label: 'Minute', kind: 'text', required: true, width: '90px', placeholder: '00' },
  {
    name: 'settlement_lag_days', label: 'Settles in (days)', kind: 'text', required: true,
    width: '150px', placeholder: '1',
  },
]

const DEFINE_REASONS: readonly DecideReason[] = [
  ['new_provider_arrangement', 'A new arrangement with an institution', ''],
  ['new_rail_enabled', 'An existing institution is carrying a new rail', ''],
  ['currency_added', 'The same rail in a currency we did not carry', ''],
]

const AMEND_REASONS: readonly DecideReason[] = [
  ['tariff_annex_change', 'The institution changed the terms in writing', ''],
  ['limit_change', 'The amount band moved', ''],
  ['cutoff_change', 'The settlement schedule moved', ''],
  ['availability_change', 'The rail is being switched on or off', ''],
  ['corrected_transcription', 'The live version was transcribed wrongly', ''],
]

const RETIRE_REASONS: readonly DecideReason[] = [
  ['arrangement_ended', 'The arrangement with the institution ended', ''],
  ['rail_withdrawn', 'The institution withdrew the rail', ''],
  ['sustained_outage', 'The rail has been unusable long enough to withdraw it', ''],
  ['defined_in_error', 'This offering should not have been defined', ''],
]

const REINSTATE_REASONS: readonly DecideReason[] = [
  ['arrangement_resumed', 'The arrangement is back on', ''],
  ['outage_resolved', 'The outage that prompted the retirement is over', ''],
  ['retired_in_error', 'It should not have been retired', ''],
]

const PRICE_REASONS: readonly DecideReason[] = [
  ['tariff_annex_transcribed', 'Transcribed from a tariff annex', ''],
  ['contract_renegotiated', 'The contract changed', ''],
  ['invoice_reconciled', 'An invoice showed the schedule we hold is wrong', ''],
  ['corrected_transcription', 'The live price was transcribed wrongly', ''],
]

const PRICE_RETIRE_REASONS: readonly DecideReason[] = [
  ['contract_ended', 'The contract that priced this rail ended', ''],
  ['awaiting_replacement_annex', 'The tariff lapsed and its replacement has not arrived', ''],
  ['published_in_error', 'This price should not have been published', ''],
]

/** The sentence every terms control repeats, because it is the one people miss. */
const GOVERNS_FROM_NOW =
  'The new version governs from the moment this is written — there is no way to schedule one, because no write path on this family accepts an effective date (go-wallet#897). Nothing is offered here that would be discarded before the request.'

/**
 * The terms an offering version carries, seeded from what is live.
 *
 * One function for all three controls that send a spec, so define, amend and
 * reinstate cannot drift into asking for the week in three different shapes.
 */
function specFields(live: OfferingSpec | null): readonly FormField[] {
  const value = (v: string | undefined): { value?: string } =>
    v === undefined || v === '' ? {} : { value: v }

  return [
    {
      name: 'available', label: 'Offer this rail', kind: 'select', required: true,
      options: [
        ['true', 'true — offer it'],
        ['false', 'false — configured but switched off'],
      ],
      ...value(live?.available),
      note: 'Never defaulted. An omitted flag read as false would take a live rail dark and report success.',
    },
    {
      name: 'currency', label: 'Currency', kind: 'text', required: true, mono: true,
      ...value(live?.currency),
      note: 'ISO-4217, and it qualifies BOTH ends of the band — so there is no representable version whose minimum and maximum are in different currencies. Never defaulted: EUR-only is a configuration state, not a schema one.',
    },
    {
      name: 'min_amount_minor', label: 'Minimum', kind: 'amount', required: true,
      currencyFrom: 'currency', ...value(live?.minAmountMinor),
      note: 'Minor units, as an integer. Zero is a REAL floor, so this is never defaulted — an omitted minimum read as zero accepts amounts nobody agreed to accept.',
    },
    {
      name: 'max_amount_minor', label: 'Maximum', kind: 'amount', required: false,
      currencyFrom: 'currency', ...value(live?.maxAmountMinor),
      note: 'Leave empty for NO ceiling. Empty and zero are opposite statements here, and the domain refuses a ceiling of zero — so an unbounded rail is expressed by leaving this blank and never by typing 0.',
    },
    {
      name: 'cutoff_timezone', label: 'Cutoff timezone', kind: 'text', required: true, mono: true,
      ...value(live?.timezone),
      note: 'An IANA zone — `Europe/Malta`, `Europe/Berlin` — and never an offset. An offset is wrong twice a year, and a cutoff that moves an hour on the last Sunday in March books payments to the wrong value date.',
    },
    {
      name: 'cutoffs', label: 'The week this version runs', kind: 'rows', required: false,
      columns: CUTOFF_COLUMNS, ...value(live?.cutoffs),
      note: 'The WHOLE week of the new version, never a delta — leaving it empty means the rail runs on no weekday, which is what a withdrawn rail looks like. Hour, minute and settlement days are each required on a row you start: 00:00 accepts almost nothing on the day, and a lag of 0 promises same-day settlement, so none of the three can be left to a default.',
    },
  ]
}

/** What a live version says, for seeding an edit. All strings, all optional. */
export interface OfferingSpec {
  readonly available?: string
  readonly currency?: string
  readonly minAmountMinor?: string
  readonly maxAmountMinor?: string
  readonly timezone?: string
  /** The cutoff rows, already JSON-encoded as the `rows` field carries them. */
  readonly cutoffs?: string
}

/**
 * The spec half of a body, or the reason it cannot be sent.
 *
 * # Why this refuses rather than dropping
 *
 * `rowsFrom` drops an incomplete row, which is right for a price list where a
 * half-typed line is a line somebody started. It is wrong here: a cutoff row
 * silently dropped is a weekday the rail stops running on, reported as success.
 * So every row is checked, and anything that would be dropped stops the whole
 * submission with a sentence naming the row.
 */
function specBody(v: Readonly<Record<string, string>>): Record<string, unknown> | string {
  const currency = (v['currency'] ?? '').trim()
  if (currency === '') return 'A currency is required beside the amounts, and is never defaulted.'

  const available = v['available'] ?? ''
  if (available !== 'true' && available !== 'false') {
    return 'Say whether this rail is offered. It is never defaulted: an omitted flag read as false would take a live rail dark.'
  }

  const min = integer(v['min_amount_minor'])
  if (min === null) {
    return 'The minimum must be a whole number of minor units. Zero is a real floor, so it cannot be left out.'
  }
  const rawMax = (v['max_amount_minor'] ?? '').trim()
  const max = rawMax === '' ? null : integer(rawMax)
  if (rawMax !== '' && max === null) {
    return 'The maximum must be a whole number of minor units, or empty for no ceiling. It is never zero: a ceiling of zero and no ceiling are opposite statements.'
  }

  const cutoffs = cutoffList(v['cutoffs'])
  if (typeof cutoffs === 'string') return cutoffs

  const body: Record<string, unknown> = {
    available: available === 'true',
    currency,
    min_amount_minor: min,
    cutoff_timezone: (v['cutoff_timezone'] ?? '').trim(),
    // Always sent, and `[]` is a real statement: the rail runs on no weekday.
    cutoffs,
  }
  // Absent rather than zero. Nil is unbounded; the domain refuses a zero
  // ceiling, so the two can never be confused at either end.
  if (max !== null) body['max_amount_minor'] = max
  return body
}

/**
 * The week, as the wire wants it, or the reason it cannot be sent.
 *
 * A duplicate weekday is refused HERE as well as at the backend, and for the
 * reason the backend gives about the JSON: two cutoffs on one weekday is
 * *"an offering whose value date depends on the order of a JSON array"*.
 * Refusing it before the request means the operator sees which row rather than
 * a 400 about an index.
 */
function cutoffList(raw: string | undefined): readonly Record<string, unknown>[] | string {
  const rows = rowsFrom(raw, CUTOFF_COLUMNS)

  // `rowsFrom` drops an incomplete row. On a price list that is right; here a
  // dropped row is a weekday the rail stops running on, so a half-filled week
  // stops the submission instead.
  const started = startedRows(raw)
  if (started > rows.length) {
    return `${String(started - rows.length)} cutoff row(s) are incomplete. Weekday, hour, minute and settlement days are each required on a row you have started — a row left half-filled would be dropped, and a dropped row is a weekday this rail stops running on.`
  }

  const seen = new Set<string>()
  const out: Record<string, unknown>[] = []
  for (const row of rows) {
    const weekday = String(row['weekday'] ?? '')
    if (seen.has(weekday)) {
      return `${weekday} appears twice. An offering has one cutoff per weekday, and two would make its value date depend on the order of a list.`
    }
    seen.add(weekday)

    const hour = integer(String(row['hour'] ?? ''))
    const minute = integer(String(row['minute'] ?? ''))
    const lag = integer(String(row['settlement_lag_days'] ?? ''))
    if (hour === null || minute === null || lag === null) {
      return `${weekday}: hour, minute and settlement days must each be whole numbers.`
    }
    if (hour < 0 || hour > 23 || minute < 0 || minute > 59) {
      return `${weekday}: ${String(hour)}:${String(minute)} is not a time of day.`
    }
    if (lag < 0) {
      return `${weekday}: settlement cannot be a negative number of days.`
    }
    out.push({ weekday, hour, minute, settlement_lag_days: lag })
  }
  return out
}

/** How many rows the operator has begun, whether or not they finished them. */
function startedRows(raw: string | undefined): number {
  if (raw === undefined || raw.trim() === '') return 0
  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    return 0
  }
  if (!Array.isArray(parsed)) return 0
  return parsed.filter((entry) => {
    if (typeof entry !== 'object' || entry === null) return false
    const row = entry as Record<string, unknown>
    return CUTOFF_COLUMNS.some((c) => typeof row[c.name] === 'string' && row[c.name] !== '')
  }).length
}

/** A whole number, or `null`. Never `parseInt`, which truncates `"12abc"`. */
function integer(raw: string | undefined): number | null {
  const v = (raw ?? '').trim()
  if (!/^-?\d+$/.test(v)) return null
  const n = BigInt(v)
  if (n > BigInt(Number.MAX_SAFE_INTEGER) || n < BigInt(-Number.MAX_SAFE_INTEGER)) return null
  return Number(n)
}

/** A refusal the operator reads, without anything having been sent. */
function refusedBeforeSending(reason: string): FormPanel {
  return {
    tone: 'r',
    heading: 'Nothing was sent',
    lines: [],
    note: `${reason} The request was not made, so nothing changed — correct it here and submit again.`,
    final: false,
  }
}

// ---------------------------------------------------------------------------
// The six controls
// ---------------------------------------------------------------------------

/** Defining a rail that does not exist yet. */
export function defineOfferingForm(api: ApiClient, caller: Caller): FormBlock {
  const blocked = writeRefusal(caller, TERMS_PERM, TERMS_ROLE, 'Defining an offering')

  return {
    t: 'form',
    title: 'Define an offering',
    lead: `An offering is one institution carrying one method in one direction in one currency. The four together are its identity and none of them can ever be changed — a transaction, a route and a buy price all name it, so renaming would re-point every one of them at a different arrangement. ${GOVERNS_FROM_NOW}`,
    fields: [
      {
        name: 'offering_key', label: 'Offering key', kind: 'text', required: true, mono: true,
        note: 'The identity, and it cannot be renamed afterwards. A lowercase slug — `emi_sepa_out`.',
      },
      {
        name: 'provider_key', label: 'Institution', kind: 'text', required: true, mono: true,
        note: 'Part of the identity, and settable only here.',
      },
      {
        name: 'method_code', label: 'Method', kind: 'text', required: true, mono: true,
        note: 'Part of the identity, and settable only here.',
      },
      {
        name: 'direction', label: 'Direction', kind: 'select', required: true, options: DIRECTIONS,
        note: '`both` is a payment method’s capability and never an offering’s: the limits, the cutoff and the buy price of an inbound credit have nothing to do with those of an outbound payment, so a rail carried both ways is TWO offerings.',
      },
      ...specFields(null),
    ],
    reasons: DEFINE_REASONS,
    reasonNote: 'Recorded in the audit row against `offering:<key>`.',
    auditLine: 'admin.offering.define · actor · offering:<key> · terms + reason',
    cta: 'Define at version 1',
    ctaNote: 'It will have NO buy price. Until one is published every margin figure that touches this rail reads as a cost nobody recorded — which is the honest state, and not the same as free.',
    blocked,
    ...(blocked === '' ? { submit: defineSubmit(api) } : {}),
  }
}

function defineSubmit(api: ApiClient) {
  return async (v: Readonly<Record<string, string>>): Promise<FormPanel> => {
    const spec = specBody(v)
    if (typeof spec === 'string') return refusedBeforeSending(spec)
    return submitWrite(
      () => api.post<unknown>('/offerings', {
        offering_key: (v['offering_key'] ?? '').trim(),
        provider_key: (v['provider_key'] ?? '').trim(),
        method_code: (v['method_code'] ?? '').trim(),
        direction: v['direction'] ?? '',
        ...spec,
        reason: v['reason'] ?? '',
      }),
      'The offering is defined',
      panelLines,
    )
  }
}

/**
 * Amending the terms — law 6's editor.
 *
 * Labelled "Save as new version" and seeded from what is live, so the operator
 * edits the version in force. It does not offer an `effective_from`; see the
 * module header and go-wallet#897.
 */
export function amendOfferingForm(
  api: ApiClient, caller: Caller, live: OfferingSpec | null,
): FormBlock {
  const blocked = writeRefusal(caller, TERMS_PERM, TERMS_ROLE, 'Amending an offering')

  return {
    t: 'form',
    title: 'Amend an offering — save as a new version',
    lead: `Nothing is edited. A new version opens and the live one closes at the same instant, so a payment accepted under the old terms still resolves to them for ever. The fields start at what is live${live === null ? ' where this console could read it' : ''}, so what you do not touch is visibly unchanged. ${GOVERNS_FROM_NOW}`,
    fields: [
      {
        name: 'offering_key', label: 'Offering key', kind: 'text', required: true, mono: true,
        note: 'The identity, from the table above. It is in the path and cannot be changed by this act.',
      },
      ...specFields(live),
    ],
    reasons: AMEND_REASONS,
    reasonNote: 'Recorded in the audit row. What an institution changed in writing belongs here in a sentence.',
    auditLine: 'admin.offering.amend · actor · offering:<key> · terms + reason',
    cta: 'Save as new version',
    ctaNote: 'The version number is assigned by the write path. The previous version stays exactly where it was — it is not moved, edited or deleted.',
    blocked,
    ...(blocked === '' ? { submit: keyedSpecSubmit(api, 'versions', 'The offering has a new version') } : {}),
  }
}

/** Bringing a retired rail back. A separate route, and a separate audit event. */
export function reinstateOfferingForm(
  api: ApiClient, caller: Caller, live: OfferingSpec | null,
): FormBlock {
  const blocked = writeRefusal(caller, TERMS_PERM, TERMS_ROLE, 'Reinstating an offering')

  return {
    t: 'form',
    title: 'Reinstate a retired offering',
    lead: `A separate act from an amendment, and separately audited: an amendment on a rail that was dark for three weeks would record the new terms and lose the fact that it came back. The new version continues from the highest number ever issued — never the retired one again, and never 1. ${GOVERNS_FROM_NOW}`,
    fields: [
      {
        name: 'offering_key', label: 'Offering key', kind: 'text', required: true, mono: true,
        note: 'A key whose versions exist but none is live. A key with no versions at all is the Define case instead.',
      },
      ...specFields(live),
    ],
    reasons: REINSTATE_REASONS,
    reasonNote: 'Recorded in the audit row against `offering:<key>`.',
    auditLine: 'admin.offering.reinstate · actor · offering:<key> · terms + reason',
    cta: 'Reinstate with these terms',
    ctaNote: 'The versions that governed before the retirement are untouched and still explain every payment accepted under them.',
    blocked,
    ...(blocked === '' ? { submit: keyedSpecSubmit(api, 'reinstate', 'The offering is reinstated') } : {}),
  }
}

function keyedSpecSubmit(api: ApiClient, verb: 'versions' | 'reinstate', heading: string) {
  return async (v: Readonly<Record<string, string>>): Promise<FormPanel> => {
    const key = (v['offering_key'] ?? '').trim()
    if (key === '') return refusedBeforeSending('An offering key is required.')
    const spec = specBody(v)
    if (typeof spec === 'string') return refusedBeforeSending(spec)
    return submitWrite(
      () => api.post<unknown>(
        `/offerings/${encodeURIComponent(key)}/${verb}`,
        { ...spec, reason: v['reason'] ?? '' },
      ),
      heading,
      panelLines,
    )
  }
}

/**
 * Withdrawing a rail.
 *
 * A typed confirmation, because it is one-way from the operator's point of view
 * and the difference between `eur-out` and `eur-in` is invisible to a dialog
 * that only asks whether you are sure. Nothing is deleted — the gap is bounded
 * on both sides, *"because withdrawing a rail during an outage must not be the
 * last thing anyone can do to it."*
 */
export function retireOfferingForm(api: ApiClient, caller: Caller, key: string): FormBlock {
  const blocked = writeRefusal(caller, TERMS_PERM, TERMS_ROLE, 'Retiring an offering')

  return {
    t: 'form',
    title: 'Retire an offering',
    lead: 'The live version closes and nothing succeeds it: routing has no candidate on this offering. Nothing is deleted — every buy price and every past payment that cited it still resolves — and it can be brought back with reinstate.',
    fields: [{
      name: 'offering_key', label: 'Offering key', kind: 'text', required: true, mono: true,
      ...(key === '' ? {} : { value: key }),
      note: 'From the table above.',
    }],
    reasons: RETIRE_REASONS,
    reasonNote: 'Recorded in the audit row. Why a rail went dark is the question asked of this row afterwards.',
    auditLine: 'admin.offering.retire · actor · offering:<key> · reason',
    cta: 'Retire this offering',
    ctaNote: 'Reversible through reinstate, which opens a new version rather than reopening this one.',
    ...(key === '' ? {} : { confirm: key }),
    blocked,
    ...(blocked === '' ? { submit: keyedReasonSubmit(api, 'retire', 'The offering is retired') } : {}),
  }
}

/**
 * Publishing what an institution charges us — law 6's editor on the tariff.
 *
 * Finance's. Every operand is required and none is defaulted, and the two that
 * look most defaultable matter most: `rounding`, because a defaulted rule is a
 * cost systematically wrong in one direction on every transaction, and
 * `source_reference`, because *"a cost figure with no provenance is one nobody
 * can defend in a margin review."*
 */
export function publishBuyPriceForm(api: ApiClient, caller: Caller): FormBlock {
  const blocked = writeRefusal(caller, PRICE_PERM, PRICE_ROLE, 'Publishing a buy price')

  return {
    t: 'form',
    title: 'Publish a buy price — save as a new version',
    lead: `What this rail costs US, per transaction: flat plus a rate, bounded by an optional floor and cap on the TOTAL. Whatever is live closes and the new version governs from the same instant; the one it replaces stays, because a transaction priced under it has to keep resolving. ${GOVERNS_FROM_NOW}`,
    fields: [
      {
        name: 'offering_key', label: 'Offering key', kind: 'text', required: true, mono: true,
        note: 'Publishing against a RETIRED offering is allowed on purpose — a tariff arrives for a dark rail too, and refusing it would mean recording the cost only after somebody remembered to reinstate.',
      },
      {
        name: 'currency', label: 'Currency', kind: 'text', required: true, mono: true,
        note: 'Must be the offering’s own. A cost in another currency is a margin figure that cannot tie to the ledger, and the backend refuses the mismatch naming both.',
      },
      {
        name: 'flat_minor', label: 'Flat component', kind: 'amount', required: true,
        currencyFrom: 'currency',
        note: 'Minor units. Zero is a REAL price — a purely proportional schedule has no flat component — so it is typed rather than left out.',
      },
      {
        name: 'percent_bps', label: 'Rate (basis points)', kind: 'text', required: true, mono: true,
        note: 'Zero is a REAL rate — a purely fixed schedule has none. 25 bps is 0.25%.',
      },
      {
        name: 'min_cost_minor', label: 'Floor on the total', kind: 'amount', required: false,
        currencyFrom: 'currency',
        note: 'Leave empty for none. It bounds the TOTAL, not the proportional part — which is what “minimum 0.20, maximum 5.00” means on every schedule anybody has sent.',
      },
      {
        name: 'max_cost_minor', label: 'Cap on the total', kind: 'amount', required: false,
        currencyFrom: 'currency',
        note: 'Leave empty for none. Empty and zero are opposite statements.',
      },
      {
        name: 'rounding', label: 'How THEY round', kind: 'select', required: true, options: ROUNDINGS,
        note: 'Not ours to choose — it is whatever the counterparty’s schedule says. Our sell side always floors because that favours the customer; assuming floor here when they round up understates our cost and overstates our margin on every single transaction.',
      },
      {
        name: 'source_reference', label: 'Transcribed from', kind: 'text', required: true,
        note: 'The contract, the tariff annex or the invoice line these figures came from. This is the whole reason this record exists rather than a report being trusted — a cost with no provenance is one nobody can defend in a margin review.',
      },
    ],
    reasons: PRICE_REASONS,
    reasonNote: 'Recorded in the audit row, beside the source reference the figures were taken from.',
    auditLine: 'admin.offering.buy_price.publish · actor · offering:<key> · price + reason',
    cta: 'Save as new version',
    ctaNote: 'The version number is assigned by the write path. A published buy price is never edited — a correction is another version.',
    blocked,
    ...(blocked === '' ? { submit: publishPriceSubmit(api) } : {}),
  }
}

function publishPriceSubmit(api: ApiClient) {
  return async (v: Readonly<Record<string, string>>): Promise<FormPanel> => {
    const key = (v['offering_key'] ?? '').trim()
    if (key === '') return refusedBeforeSending('An offering key is required.')
    const currency = (v['currency'] ?? '').trim()
    if (currency === '') return refusedBeforeSending('A currency is required beside every amount.')

    const flat = integer(v['flat_minor'])
    const bps = integer(v['percent_bps'])
    if (flat === null || bps === null) {
      return refusedBeforeSending('The flat component and the rate must each be whole numbers, and neither is defaulted: zero is a real value for both.')
    }
    const body: Record<string, unknown> = {
      currency,
      flat_minor: flat,
      percent_bps: bps,
      rounding: v['rounding'] ?? '',
      source_reference: (v['source_reference'] ?? '').trim(),
      reason: v['reason'] ?? '',
    }
    for (const [field, key2] of [
      ['min_cost_minor', 'min_cost_minor'], ['max_cost_minor', 'max_cost_minor'],
    ] as const) {
      const raw = (v[field] ?? '').trim()
      if (raw === '') continue
      const n = integer(raw)
      if (n === null) {
        return refusedBeforeSending(`${field} must be a whole number of minor units, or empty for none.`)
      }
      body[key2] = n
    }

    return submitWrite(
      () => api.post<unknown>(`/offerings/${encodeURIComponent(key)}/buy-prices`, body),
      'The buy price is published',
      panelLines,
    )
  }
}

/**
 * Closing the live buy price with nothing to succeed it.
 *
 * The consequence is spelled out at the control because the wrong reading is
 * the expensive one: no price governing means *"we do not know what this
 * costs"*, never *"it is free"*.
 */
export function retireBuyPriceForm(api: ApiClient, caller: Caller, key: string): FormBlock {
  const blocked = writeRefusal(caller, PRICE_PERM, PRICE_ROLE, 'Retiring a buy price')

  return {
    t: 'form',
    title: 'Retire the live buy price',
    lead: 'The live price closes and nothing succeeds it. This offering then has NO cost governing — which every reader must treat as unknown rather than as free, and which is an honest state to be able to record between a contract ending and its replacement arriving.',
    fields: [{
      name: 'offering_key', label: 'Offering key', kind: 'text', required: true, mono: true,
      ...(key === '' ? {} : { value: key }),
      note: 'From the table above.',
    }],
    reasons: PRICE_RETIRE_REASONS,
    reasonNote: 'Recorded in the audit row against `offering:<key>`.',
    auditLine: 'admin.offering.buy_price.retire · actor · offering:<key> · reason',
    cta: 'Retire the buy price',
    ctaNote: 'Every margin figure that touches this rail will report an unrecorded cost until a new price is published. The retired version stays and still prices the transactions it governed.',
    ...(key === '' ? {} : { confirm: key }),
    blocked,
    ...(blocked === '' ? { submit: keyedReasonSubmit(api, 'buy-prices/retire', 'The buy price is retired') } : {}),
  }
}

function keyedReasonSubmit(api: ApiClient, verb: string, heading: string) {
  return async (v: Readonly<Record<string, string>>): Promise<FormPanel> => {
    const key = (v['offering_key'] ?? '').trim()
    if (key === '') return refusedBeforeSending('An offering key is required.')
    return submitWrite(
      () => api.post<unknown>(
        `/offerings/${encodeURIComponent(key)}/${verb}`,
        { reason: v['reason'] ?? '' },
      ),
      heading,
      panelLines,
    )
  }
}

/**
 * What the backend answered.
 *
 * The `note` on every one of these responses says something an operator needs
 * and would otherwise have to be told twice — that a defined offering has no
 * buy price yet, that a retired one can come back, that no cost governing means
 * unknown — so it is surfaced rather than dropped.
 */
function panelLines(answer: unknown): readonly FormLine[] {
  const lines: FormLine[] = []
  for (const [key, label] of [
    ['offering_key', 'offering'], ['version', 'version'], ['state', 'state'],
  ] as const) {
    const v = valueAt(answer, key)
    if (v !== '') lines.push([label, v, ''])
  }
  const note = valueAt(answer, 'note')
  if (note !== '') lines.push(['the backend adds', note, ''])
  return lines
}

function valueAt(o: unknown, key: string): string {
  if (typeof o !== 'object' || o === null) return ''
  const v = (o as Record<string, unknown>)[key]
  if (typeof v === 'string') return v
  if (typeof v === 'number') return String(v)
  return ''
}

/**
 * The six controls, Ops' four then Finance's two.
 *
 * All six are drawn for every session (law 18). A session holding one pair sees
 * the other pair disabled with the role that holds it named — the split is
 * deliberate, and hiding half of it would make it look like a gap.
 */
export function offeringControls(api: ApiClient, caller: Caller): readonly Block[] {
  return [
    defineOfferingForm(api, caller),
    amendOfferingForm(api, caller, null),
    retireOfferingForm(api, caller, ''),
    reinstateOfferingForm(api, caller, null),
    publishBuyPriceForm(api, caller),
    retireBuyPriceForm(api, caller, ''),
  ]
}
